<template>
  <div v-if="loaded" class="container is-fluid" ref="container">
    <section class="section">
      <Breadcrumb
        :items="[
          {
            path: '/bnpl-deals',
            name: 'mp:breadcrumb.offer.bnpl_deal.list',
          },
          {
            path: `/bnpl-deal/${given}`,
            name: 'mp:breadcrumb.offer.bnpl_deal.edit',
          },
        ]"
      />
    </section>

    <section class="section">
      <div class="mb-4 is-flex is-align-items-center">
        <span class="is-size-4 has-text-weight-bold"
          >{{ $t("bnpl:deal.title.edit") }} {{ bnplDeal.id }}</span
        >

        <div class="ml-auto">
          <b-button
            v-if="refundVisible"
            class="is-size-7 has-text-danger mx-4 is-outlined"
            @click="refundConfirmModal()"
            type="is-danger"
            >{{ $t("bnpl:deal.button.refund") }}
          </b-button>

          <span
            v-if="loaded && !approveVisible"
            class="status-badge status-badge-blue"
          >
            <span class="has-text-white">{{
              $t(BNPL_APPLICATION_STATUS_TOKENS[application.currentStatus])
            }}</span>
          </span>

          <span
            v-if="loaded && !refundVisible"
            class="status-badge status-badge-red ml-4"
          >
            <span class="has-text-white">{{
              $t(
                BNPL_APPLICATION_STATUS_RESULT_TOKENS[
                  application.currentStatusResult
                ]
              )
            }}</span>
          </span>
        </div>
      </div>

      <div class="columns">
        <div class="column is-two-thirds">
          <div class="entity-details-card">
            <div class="is-flex is-flex-direction-column">
              <div class="is-flex is-justify-content-space-between mb-3">
                <div v-if="isLate" class="mr-3">
                  <BnplTransactionsDelay
                    :due-date="lateSince"
                    :sum="lateSum"
                  ></BnplTransactionsDelay>
                </div>
                <div class="deal-status">
                  {{ $t(BNPL_DEAL_STATUS_TOKENS[bnplDeal.status]) }}
                </div>
              </div>

              <div
                style="
                  display: grid;
                  grid-template-columns: repeat(2, minmax(0, 1fr));
                  grid-gap: 24px;
                "
              >
                <div>
                  <p class="is-size-7 is-uppercase mb-1 has-text-grey-icon">
                    {{ $t("mp:deal.transaction.header.cart.value") }}
                  </p>
                  <span class="has-text-weight-medium">{{
                    formatMoney(application.cartValue)
                  }}</span>
                </div>

                <div>
                  <p class="is-size-7 is-uppercase mb-1 has-text-grey-icon">
                    {{ $t("mp:deal.transaction.header.total.repayable") }}
                  </p>
                  <span class="has-text-weight-medium">{{
                    formatMoney(bnplDeal.totalRepayable)
                  }}</span>
                </div>

                <div>
                  <p class="is-size-7 is-uppercase mb-1 has-text-grey-icon">
                    {{
                      $t("mp:deal.transaction.total.number.of.fragments") +
                      "/" +
                      $t("mp:deal.transaction.paid.number.of.fragments")
                    }}
                  </p>
                  <span class="has-text-weight-medium"
                    >{{ bnplDeal.successfulTransactions }} /
                    {{ bnplDeal.transactions.length }}</span
                  >
                </div>
                <!--              AMOUNT RECEIVED-->
                <div>
                  <p class="is-size-7 is-uppercase mb-1 has-text-grey-icon">
                    {{ $t("mp:deal.transaction.amount.received") }}
                  </p>
                  <span class="has-text-weight-medium">{{
                    formatMoney(bnplDeal.successfulTransactionsAmount)
                  }}</span>
                </div>
              </div>
              <hr />
              <div
                style="
                  display: grid;
                  grid-template-columns: repeat(3, minmax(0, 1fr));
                  grid-gap: 24px;
                "
              >
                <div>
                  <p class="is-size-7 is-uppercase mb-1 has-text-grey-icon">
                    {{
                      $t("mp:deal.header.date.panel.contract.conclusion.date")
                    }}
                  </p>
                  <span class="has-text-weight-medium">
                    {{ formatDate(bnplDeal.created) }}
                  </span>
                </div>
                <div>
                  <p class="is-size-7 is-uppercase mb-1 has-text-grey-icon">
                    {{ $t("mp:deal.header.date.panel.time.of.first.payment") }}
                  </p>
                  <span class="has-text-weight-medium">
                    {{ firstTransactionDate }}
                  </span>
                </div>
                <div>
                  <p class="is-size-7 is-uppercase mb-1 has-text-grey-icon">
                    {{ $t("mp:deal.header.date.panel.expiry.date") }}
                  </p>
                  <span class="has-text-weight-medium"> {{ dealDueDate }}</span>
                </div>
                <div>
                  <p class="is-size-7 is-uppercase mb-1 has-text-grey-icon">
                    {{ $t("mp:deal.header.date.panel.order.id") }}
                  </p>
                  <span class="has-text-weight-medium">
                    {{ bnplDeal.orderId }}</span
                  >
                </div>
              </div>
            </div>
          </div>
        </div>
        <b-skeleton v-if="!loaded" height="140"></b-skeleton>

        <div class="column">
          <div v-if="loaded" class="entity-details-card">
            <div
              class="is-flex is-flex-direction-column is-justify-content-space-between"
              style="flex-grow: 1"
            >
              <div>
                <p class="is-size-7 is-uppercase mb-1 has-text-grey-icon">
                  {{ $t("mp:deal.customer.details.name") }}
                </p>
                <span class="has-text-weight-medium">
                  {{ application.customerData.customerLastName }}
                  {{ application.customerData.customerFirstName }}
                </span>
                <span
                  v-if="
                    !application.customerData.customerLastName &&
                    !application.customerData.customerFirstName
                  "
                  class="has-text-weight-medium"
                >
                  {{ $t("mp:no_data") }}
                </span>
              </div>
              <div>
                <p class="is-size-7 is-uppercase mb-1 has-text-grey-icon">
                  {{ $t("mp:deal.customer.details.phone") }}
                </p>
                <span class="has-text-weight-medium">
                  {{ application.customerData.customerPhone }}
                </span>
              </div>
              <div>
                <p class="is-size-7 is-uppercase mb-1 has-text-grey-icon">
                  {{ $t("mp:deal.customer.details.email") }}
                </p>
                <span class="has-text-weight-medium">
                  {{ application.customerData.customerEmail }}
                </span>
              </div>
            </div>
          </div>

          <b-skeleton v-else height="140"></b-skeleton>
        </div>
      </div>
    </section>

    <section class="section">
      <b-tabs v-model="activeTab" class="mb-0 mt-4">
        <b-tab-item
          :value="TABS.TRANSACTIONS"
          :label="$t('bnpl:deal.transaction.tab.transactions')"
        />
        <b-tab-item :value="TABS.CART" :label="$t('bnpl:deal.tab.cart')" />
      </b-tabs>

      <!-- ÜGYLET -->
      <template v-if="loaded">
        <!-- KOSÁR -->
        <template v-if="activeTab === TABS.CART">
          <BnplPurchaseCartTile
            :purchase-id="bnplDeal.purchaseId"
          ></BnplPurchaseCartTile>
        </template>

        <template v-if="activeTab === TABS.TRANSACTIONS">
          <BnplTransactionsTile
            :deal="bnplDeal"
            :transactions="bnplDeal.transactions"
          >
          </BnplTransactionsTile>
        </template>

        <note-component
          :entityName="NOTE_ENTITY_NAME.BNPL_APPLICATION"
          :entityId="bnplDeal.applicationId"
        />
      </template>
      <b-skeleton v-else height="80"></b-skeleton>
    </section>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { formatMoney, formatDate, formatDateTime } from "@/utils/util";
import {
  BNPL_APPLICATION_STATUS_RESULT_TOKENS,
  BNPL_APPLICATION_STATUS_TOKENS,
  BNPL_DEAL_STATUS,
  BNPL_TRANSACTION_STATUS,
  BNPL_DEAL_STATUS_TOKENS,
  BNPL_DEAL_STATUS_RESULT,
  BNPL_DEAL_STATUS_RESULT_TOKENS,
  LOAN_APPLICATION_DECISION_MODAL_TYPE,
  NOTE_ENTITY_NAME,
  BNPL_DEAL_TRANSACTION_ATTEMPT_STATUS,
} from "@/utils/const";
import { AUTHORITIES } from "@/utils/authorities";
import BnplPurchaseCartTile from "@/components/deal/BnplPurchaseCartTile";
import BnplTransactionsTile from "@/components/deal/BnplTransactionsTile.vue";
import BnplTransactionsDelay from "@/components/deal/BnplTransactionDelay.vue";
import i18n from "@/plugins/i18n";

const TABS = {
  BASE_DATE: "baseData",
  UPLOADED_DOCUMENTS: "uploadDocuments",
  STATEMENTS: "statements",
  IDENTIFICATION: "identification",
  DOC_GEN: "offerDocGenerations",
  FINAL_DOCUMENTS: "finalDocuments",
  HISTORY: "history",
  CART: "cart",
  TRANSACTIONS: "transactions",
};

export default {
  name: "BnplDeals",
  title: "mp:page.bnpl_deal.title",
  components: {
    BnplTransactionsTile,
    BnplTransactionsDelay,
    BnplPurchaseCartTile,
    Breadcrumb: () => import("@/components/Breadcrumb.vue"),
  },
  props: {
    given: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      TABS,
      BNPL_APPLICATION_STATUS_RESULT_TOKENS,
      BNPL_APPLICATION_STATUS_TOKENS,
      BNPL_DEAL_STATUS,
      BNPL_TRANSACTION_STATUS,
      BNPL_DEAL_STATUS_TOKENS,
      BNPL_DEAL_STATUS_RESULT,
      BNPL_DEAL_STATUS_RESULT_TOKENS,
      LOAN_APPLICATION_DECISION_MODAL_TYPE,
      NOTE_ENTITY_NAME,
      BNPL_DEAL_TRANSACTION_ATTEMPT_STATUS,
      activeTab: TABS.BASE_DATE,
      application: {},
      bnplDeal: {},
      flow: {},
      documents: [],
      correctionDocuments: [],
      preliminaryAssessment: {
        dynamicData: {},
      },
      bnplDealBaseData: {}, // actual calculations
      loaded: false,
    };
  },
  computed: {
    ...mapGetters({
      userId: "session/userId",
      bnplDealGetter: "deal/get",
    }),
    refundVisible() {
      return this.loaded && this.bnplDeal.status !== "CANCELLED";
    },
    isLate() {
      return this.bnplDeal?.transactions?.some((it) => it.isLate);
    },
    lateSum() {
      return this.bnplDeal?.transactions
        ?.filter((it) => it.isLate)
        .reduce((acc, it) => acc + it.sum, 0);
    },
    lateSince() {
      if (!this.isLate) return null;
      return this.bnplDeal?.transactions?.filter((it) => it.isLate)[0].dueDate;
    },
    canSeeSignature() {
      return this.$store.getters["session/hasAnyAuthority"]([
        AUTHORITIES.CREDIT.LOAN_APPLICATION.ADMIN,
        AUTHORITIES.CREDIT.LOAN_APPLICATION.SIGNATURE.GET,
      ]);
    },
    canSeeCorrection() {
      return this.$store.getters["session/hasAnyAuthority"]([
        AUTHORITIES.CREDIT.LOAN_APPLICATION.ADMIN,
        AUTHORITIES.CREDIT.LOAN_APPLICATION.CORRECTION.GET,
      ]);
    },
    canSeeScoring() {
      return this.$store.getters["session/hasAnyAuthority"]([
        AUTHORITIES.SCORING.SCORE.GET,
        AUTHORITIES.SCORING.SCORE.ADMIN,
      ]);
    },
    canSeeOfferBaseData() {
      return this.$store.getters["session/hasAnyAuthority"]([
        AUTHORITIES.CREDIT.LOAN_APPLICATION.ADMIN,
        AUTHORITIES.CREDIT.LOAN_APPLICATION.OFFER_BASE_DATA.LIST,
      ]);
    },
    firstTransactionDate() {
      if (!!this.bnplDeal?.transactions) {
        const firstTransaction = this.bnplDeal.transactions.find(
          (it) => it.seq === 1
        );
        if (!!firstTransaction?.attempts) {
          const successfulAttempt = firstTransaction.attempts.find(
            (it) =>
              it.result === BNPL_DEAL_TRANSACTION_ATTEMPT_STATUS.ENUM.SUCCESS
          );
          if (!!successfulAttempt) {
            return formatDate(successfulAttempt.timestamp);
          }
        }
        return "-";
      }
      return "-";
    },
    dealDueDate() {
      if (!!this.bnplDeal?.transactions) {
        return formatDate(this.bnplDeal.transactions.slice(-1)[0].dueDate);
      }
      return "-";
    },
    approveVisible() {
      return (
        this.loaded &&
        this.bnplDeal.currentStatus !== "CLOSED" &&
        this.bnplDeal.currentStatus !== "BANK_RESULT" &&
        this.bnplDeal.currentStatus !== "PURCHASE_BANK_RESULT" &&
        this.bnplDeal.currentStatus !== "COMPLETED"
      );
    },
    editVisible() {
      return (
        this.loaded &&
        this.bnplDeal.currentStatus !== "CLOSED" &&
        this.bnplDeal.currentStatus !== "COMPLETED"
      );
    },
    approveDisabled() {
      return (
        this.loaded &&
        this.bnplDeal.currentStatus !== "UNDER_REVIEW" &&
        this.bnplDeal.currentStatus !== "OFFLINE_UNDER_REVIEW"
      );
    },
    inApprovalPeriod() {
      return this.bnplDeal.currentStatus !== this.bnplDeal.currentStatus; //BNPL_DEAL_STATUS.UNDER_REVIEW;
    },
  },
  async created() {
    await this.fetch();
    this.loaded = true;
  },
  methods: {
    formatDateTime,
    formatDate,
    formatMoney,
    async fetch() {
      //Fetch deal
      await this.$store.dispatch("deal/fetch", this.given);
      this.bnplDeal = this.$store.getters["deal/get"](this.given);
      this.bnplDeal.transactions.sort(
        (a, b) => new Date(a.dueDate) - new Date(b.dueDate)
      );
      this.bnplDeal.totalRepayable = this.bnplDeal.transactions.reduce(
        (a, b) => +a + +b.sum,
        0
      );
      await this.$store.dispatch(
        "bnplApplication/fetch",
        this.bnplDeal.applicationId
      );
      this.application = this.$store.getters["bnplApplication/get"](
        this.bnplDeal.applicationId
      );

      let successfulTransactions = 0;
      let successfulTransactionsAmount = 0;
      this.bnplDeal.transactions.forEach((value) => {
        if (value.status === BNPL_TRANSACTION_STATUS.ENUM.PAID) {
          ++successfulTransactions;
          successfulTransactionsAmount += value.sum;
        }
      });
      this.bnplDeal.successfulTransactions = successfulTransactions;
      this.bnplDeal.successfulTransactionsAmount = successfulTransactionsAmount;

      this.activeTab = TABS.TRANSACTIONS;
    },
    refundConfirmModal() {
      this.$buefy.dialog.confirm({
        title: i18n.t("bnpl:deal.delete_confirm.button.title"),
        message: i18n.t("bnpl:deal.delete_confirm.button.message"),
        cancelText: i18n.t("bnpl:deal.delete_confirm.button.cancel"),
        confirmText: i18n.t("bnpl:deal.delete_confirm.button.confirm"),
        type: "is-danger",
        hasIcon: true,
        onConfirm: () => this.refundDeal(),
      });
    },
    async refundDeal() {
      await this.$store.dispatch("deal/refund", this.application.id);
      await this.$router.push("/deals");
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/colors.scss";

.tile-empty {
  background: #dfe6ef;
  border-radius: 8px;
}

.has-text-danger:focus,
.has-text-danger:hover {
  color: white !important;
}

.has-text-danger:focus[disabled],
.has-text-danger:hover[disabled] {
  color: #f14668 !important;
}

.tile {
  background: #ffffff;
  border-radius: 8px;
  display: flex;
  align-items: center;
}

.deal-status {
  background-color: $grey-lightest;
  color: $blue;
  border-radius: 40px;
  padding: 8px 12px;
  font-weight: 700;
}
</style>
